import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, shareReplay } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { createActionStream } from '@core/ngrx-legacy';
import { TellentCurrentUserEntityActions, TellentCurrentUserSelectors } from '@core/tellent-data';
import {
  type OrgFeatures,
  SeatKind,
  TellentCurrentUser,
  TellentCurrentUserUpdate,
  TellentPasswordChange,
} from '@core/tellent-types';

@Injectable({ providedIn: 'root' })
export class TellentCurrentUserDataFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public currentUser = this.store.selectSignal<TellentCurrentUser | null>(
    TellentCurrentUserSelectors.data,
  );
  public currentMember = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );
  public currentOrg = computed(() => {
    const member = this.currentMember();
    return member ? member.org : null;
  });
  public currentUser$ = this.store
    .select<TellentCurrentUser | null>(TellentCurrentUserSelectors.data)
    .pipe(filterBoolean, shareReplay({ bufferSize: 1, refCount: true }));
  public organizationMembership = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelector,
  );
  public organizationMembershipSafe = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );
  public isOrgOwner = this.store.selectSignal<boolean | null>(
    TellentCurrentUserSelectors.isOrgOwner,
  );
  public isAnyOrgOwner$ = this.store
    .select(TellentCurrentUserSelectors.isAnyOrgOwner)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  public members = toSignal(
    this.store.select(TellentCurrentUserSelectors.data).pipe(
      filterBoolean,
      map(({ members }) => members),
    ),
  );
  public currentUserGuid = computed(() => this.currentUser()?.guid);

  public updateUserSuccess$ = createActionStream(
    TellentCurrentUserEntityActions.updateCurrentUserDataSuccess,
  );
  public updateUserFail$ = createActionStream(
    TellentCurrentUserEntityActions.updateCurrentUserDataFail,
  );
  public updatePasswordSuccess$ = createActionStream(
    TellentCurrentUserEntityActions.updatePasswordSuccess,
  );
  public updatePasswordFail$ = createActionStream(
    TellentCurrentUserEntityActions.updatePasswordFail,
  );

  public seatByKind(kind: SeatKind) {
    return this.store.selectSignal(TellentCurrentUserSelectors.seatByKindSelector(kind));
  }

  public fetch() {
    this.store.dispatch(TellentCurrentUserEntityActions.fetch());
  }

  public leaveCompany(orgGuid: string) {
    this.store.dispatch(TellentCurrentUserEntityActions.leaveCompany({ payload: { orgGuid } }));
  }

  public deleteAccount(emailCode: string) {
    this.store.dispatch(
      TellentCurrentUserEntityActions.deleteAccount({ payload: { code: emailCode } }),
    );
  }

  public updateUser(payload: Partial<TellentCurrentUserUpdate>) {
    this.store.dispatch(TellentCurrentUserEntityActions.updateCurrentUserData({ payload }));
  }

  public updatePassword(payload: TellentPasswordChange) {
    this.store.dispatch(
      TellentCurrentUserEntityActions.updatePassword({
        payload,
      }),
    );
  }

  public getOrgFeature(feature: OrgFeatures) {
    return computed(() => {
      const features = this.currentOrg()?.features ?? [];
      return features.some(f => f === feature);
    });
  }
}
